import { Link, useParams } from "react-router-dom";

import { PageHeader } from "@/app/PageHeader";
import { useAsync } from "@/lib/useAsync";
import { formatRelative } from "@/features/devices/adapters";
import { EmptyState } from "@/features/devices/components/EmptyState";
import { ErrorState } from "@/features/devices/components/ErrorState";
import { LoadingState } from "@/features/devices/components/LoadingState";
import { listWorkspaceMachines } from "@/features/machines/api";
import { IconArrowLeft, IconLayers, IconPlus } from "@/lib/icons";

/**
 * Página legada `/workspaces/:workspaceId`. Apresenta os workflows
 * gerados a partir do workspaceId vindo da URL e oferece um atalho
 * para o claim. Acessada apenas via links externos antigos; a entrada
 * principal é `/workflows`.
 */
export function WorkspacePage() {
  const { workspaceId = "" } = useParams<{ workspaceId: string }>();
  const decodedWorkspaceId = decodeURIComponent(workspaceId);

  const { state, reload } = useAsync(
    `workspaces/${decodedWorkspaceId}/machines`,
    (signal) => listWorkspaceMachines(decodedWorkspaceId, signal),
  );

  const machines = state.data ?? [];
  const deviceCount = machines.reduce((total, machine) => total + machine.deviceCount, 0);
  const channelCount = machines.reduce((total, machine) => total + machine.channelCount, 0);
  const addHardwareUrl = `/workflows/${encodeURIComponent(decodedWorkspaceId)}?addDevice=1`;

  return (
    <>
      <PageHeader
        crumbs={[
          { label: "Dashboard", to: "/workflows" },
          { label: "Workspace (legacy)" },
          { label: decodedWorkspaceId },
        ]}
        title={decodedWorkspaceId || "Workspace"}
        meta={
          <>
            <span className="item">
              Workflows <b>{machines.length}</b>
            </span>
            <span className="item">
              Gateways <b>{deviceCount}</b>
            </span>
            <span className="item">
              Sinais <b>{channelCount}</b>
            </span>
          </>
        }
      />
      <div className="content">
        <div className="section-h">
          <h2>Workflows projetados</h2>
          <div className="right">
            <Link className="btn" to="/workflows">
              <IconArrowLeft size={12} /> Voltar aos workflows
            </Link>
            <Link
              className="btn"
              to={`/workspaces/${encodeURIComponent(decodedWorkspaceId)}/devices`}
            >
              Devices técnicos
            </Link>
          </div>
        </div>

        {state.status === "loading" && !state.data ? (
          <LoadingState label="Montando workflow..." />
        ) : null}

        {state.status === "error" ? (
          <ErrorState error={state.error} onRetry={reload} />
        ) : null}

        {state.data && machines.length === 0 ? (
          <EmptyState
            title="Workspace sem workflow"
            message="Adicione um dispositivo GHM pelo mqttId. O device claimado neste workspace aparece como o gateway G0 da máquina principal."
            action={
              <Link className="btn primary" to={addHardwareUrl}>
                <IconPlus size={12} /> Adicionar dispositivo
              </Link>
            }
          />
        ) : null}

        {machines.length > 0 ? (
          <div className="wgrid">
            {machines.map((machine) => (
              <Link
                key={machine.id}
                className="w"
                style={{ textDecoration: "none", color: "inherit" }}
                to={`/workflows/${encodeURIComponent(decodedWorkspaceId)}`}
              >
                <div className="w-hd">
                  <div className="w-hd-l">
                    <div className="w-title">{machine.name}</div>
                    <div className="w-sub">
                      {machine.deviceCount} gateway(s) · {machine.channelCount} sinal(is)
                    </div>
                  </div>
                  <div className="w-hd-r">
                    <span className="badge brand">
                      <IconLayers size={11} /> WF
                    </span>
                  </div>
                </div>
                <div className="w-ft">
                  <span className="ts">
                    <span className="clk" />
                    último sinal {formatRelative(machine.lastSeenAt)}
                  </span>
                  <span className={`badge ${machine.status === "online" ? "ok" : "muted"}`}>
                    <span className={machine.status === "online" ? "led-anim" : "dot"} />
                    {machine.status === "online" ? "ONLINE" : "AGUARDANDO"}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        ) : null}
      </div>
    </>
  );
}
